import { InfiniteData, QueryClient, useInfiniteQuery } from '@tanstack/react-query';

import { CandidateViewModel } from '@/entities/candidate';
import { QueryKey } from '@/types/query';
import { useMemo } from 'react';
import { BestMatchesViewModel, toBestMatchesViewModel } from '../model';
import { robinJrChatClient } from './client';
import { useGetChatQuery } from './use-get-chat-query';

const defaultSelector = <T>(data: CandidateViewModel): T => data as T;

const DEFAULT_PAGE_SIZE = 20;

export const useBestMatchesInfiniteQuery = <SelectedData = CandidateViewModel>(
  chatId: number,
  selector = defaultSelector<SelectedData>,
) => {
  const { data: chat } = useGetChatQuery(chatId);

  const query = useInfiniteQuery({
    queryKey: [QueryKey.useBestMatchesInfiniteQuery, chatId],
    queryFn: async ({ pageParam = 0 }): Promise<BestMatchesViewModel> => {
      if (!chat) throw new Error('Chat not found');
      if (!chat.campaignId) throw new Error('Chat campaign id is not found');

      const [offset, limit] = [pageParam * DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE];

      const response = await robinJrChatClient.getCandidates(chat.campaignId, offset, limit);

      return toBestMatchesViewModel(response.data);
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalCandidates = allPages.reduce((acc, page) => acc + page.candidates.length, 0);

      if (totalCandidates < lastPage.total) {
        return allPages.length;
      }

      return undefined;
    },
    enabled: !!chat && !!chat.campaignId,
    retry: false,
  });

  const data = useMemo(() => {
    return (
      query.data?.pages
        .flat()
        .map((page) => page.candidates.map(selector))
        .flat() || []
    );
  }, [query.data, selector]);

  return {
    ...query,
    dataCount: query.data?.pages?.[0]?.total || 0,
    data,
  };
};

useBestMatchesInfiniteQuery.removeCandidate = (
  queryClient: QueryClient,
  chatId: number,
  candidateEsPersonId: string,
) => {
  queryClient.setQueryData<InfiniteData<BestMatchesViewModel>>(
    [QueryKey.useBestMatchesInfiniteQuery, chatId],
    (data) => {
      if (!data) return data;

      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          candidates: page.candidates.filter((c) => c.esPersonId !== candidateEsPersonId),
        })),
      };
    },
  );
};
